import parseISO from 'date-fns/parseISO';
import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';

import LastCheckTimestamp from '~/components/logic/pageDetail/components/LastCheckTimestamp';
import LastCrawlFormatter, {
	LastCrawlFormatterMessageType,
} from '~/components/app/LastCrawlFormatter';

import {
	useLastRobotsTxtCheckInfoQuery,
} from './LastRobotsTxtCheckInfo.gql';

import usePlatformQuery from '~/hooks/usePlatformQuery';
import useWebsiteIsCrawlingPaused from '~/hooks/useWebsiteIsCrawlingPaused';
import useWindowHasFocus from '~/hooks/useWindowHasFocus';



const messages = defineMessages({
	checkingNowLabel: {
		id: 'ui.pageDetail.lastCheckedAt.label.checkingNow',
	},
	pausedMessage: {
		id: 'ui.platform.robotsTxtViewer.lastChecked.hint.paused',
	},
});



type Props = {
	websiteId: CK.WebsiteId,
};

const LastRobotsTxtCheckInfo: React.FC<Props> = (props) => {
	const {
		websiteId,
	} = props;

	const websiteIsCrawlingStopped = useWebsiteIsCrawlingPaused(websiteId);
	const windowHasFocus = useWindowHasFocus();

	const { data } = usePlatformQuery(
		useLastRobotsTxtCheckInfoQuery,
		websiteId,
	);

	const lastTimeCheckedAt = data?.platformData.canonicalRobotsTxtLastCheckedAt;

	const [timestamp, setTimestamp] = React.useState<boolean | Date>(false);

	React.useEffect(
		() => {
			if (lastTimeCheckedAt === undefined) {
				setTimestamp(false);
				return;
			}

			if (lastTimeCheckedAt === null) {
				setTimestamp(true);
				return;
			}

			setTimestamp(
				parseISO(lastTimeCheckedAt),
			);
		},
		[
			lastTimeCheckedAt,
		],
	);

	if (timestamp === false || websiteIsCrawlingStopped === null) {
		return null;
	}

	return (
		<LastCheckTimestamp
			checkedAtLabel={
				timestamp === true ? (
					<FormattedMessage {...messages.checkingNowLabel} />
				) : (
					<LastCrawlFormatter
						date={timestamp}
					/>
				)
			}
			explanationMessage={websiteIsCrawlingStopped && (
				<FormattedMessage
					{...messages.pausedMessage}
					values={{
						pastRelativeText: timestamp instanceof Date ? (
							<LastCrawlFormatter
								date={timestamp}
								messageType={LastCrawlFormatterMessageType.RobotsTxt}
							/>
						) : '',
					}}
				/>
			)}
			isPaused={websiteIsCrawlingStopped}
			lastTimeCheckedAt={lastTimeCheckedAt}
			timestamp={timestamp}
			windowHasFocus={windowHasFocus}
		/>
	);
};



export default LastRobotsTxtCheckInfo;
